import { useItem } from '../../api/queries'
import type { ManualGuide } from '../../api/types'
import { Card, ErrorState, LoadingState } from '../../components/ui'

const MATCH_LABELS: Record<string, string> = {
  exact: 'Modelo exacto', family: 'Familia del equipo', generic: 'Referencia general',
}

export function EquipmentManualGuide({ equipmentId }: { equipmentId: number }) {
  const query = useItem<ManualGuide>(`/api/equipment/${equipmentId}/manual-guide/`)
  if (query.isPending) return <LoadingState label="Cargando guía del inspector…" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const guide = query.data
  const sections = guide.sections ?? []

  return (
    <div className="manual-guide stack">
      <Card title="Guía del inspector">
        <p className="manual-guide__lead">Información de consulta para <strong>{guide.equipment_code} · {guide.equipment_name}</strong>. Síntesis en español de las fuentes oficiales, revisada el {guide.reviewed_on?.split('-').reverse().join('/')}.</p>
        <p className="muted">Las causas descritas son posibilidades para comprobar. Los ajustes, lubricantes y repuestos se deben contrastar con la placa y configuración instalada.</p>
        <nav className="manual-guide__nav" aria-label="Contenido de la guía">
          {sections.map((section) => <a key={section.key} href={`#manual-${section.key}`}>{section.title}</a>)}
          <a href="#manual-documents">Documentos y fuentes</a>
        </nav>
        <details className="manual-guide__details">
          <summary>Datos registrados de este equipo</summary>
          <p className="muted">Datos de la ficha actual; no son valores calculados a partir del manual de familia.</p>
          <div className="manual-guide__grid">
            {['Reductor', 'Motor'].map((group) => (
              <section key={group} aria-label={`Datos del ${group.toLowerCase()}`}>
                <h3>{group}</h3>
                <dl className="manual-guide__facts">
                  {guide.facts?.filter((fact) => fact.group === group).map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
                </dl>
                {!guide.facts?.some((fact) => fact.group === group) ? <p className="muted">Sin datos técnicos registrados.</p> : null}
              </section>
            ))}
          </div>
        </details>
        <details className="manual-guide__details" open>
          <summary>Secuencia de inspección en campo</summary>
          <ol className="manual-guide__checklist">{guide.checklist?.map((item) => <li key={item}>{item}</li>)}</ol>
        </details>
        {guide.missing?.length ? (
          <details className="manual-guide__details manual-guide__pending" open={!guide.has_specific_reference}>
            <summary>Datos por confirmar antes de intervenir ({guide.missing.length})</summary>
            <ul>{guide.missing.map((item) => <li key={item}>{item}</li>)}</ul>
          </details>
        ) : null}
      </Card>
      {sections.map((section) => (
        <div id={`manual-${section.key}`} key={section.key} className="manual-guide__section">
          <Card title={section.title}>
            {section.key === 'faults' ? <p className="muted">Orientación para reconocer síntomas; no implica que este equipo presente esas fallas. Registra el hallazgo y confirma la causa con mantenimiento.</p> : null}
            {section.blocks.length ? (
              <div className="manual-guide__grid">
                {section.blocks.map((block) => (
                  <article key={block.key} className="manual-guide__block">
                    <h3>{block.title}</h3>
                    <span className="manual-match" data-scope={block.match_scope}>{MATCH_LABELS[block.match_scope]}</span>
                    <ul>{block.items.map((item) => <li key={item}>{item}</li>)}</ul>
                    <footer><a href={block.source_url} target="_blank" rel="noopener noreferrer">Consultar fuente oficial ↗</a><small>{block.source_location}</small></footer>
                  </article>
                ))}
              </div>
            ) : (
              <p className="manual-guide__pending">La marca o familia de este equipo aún no tiene una referencia confirmada. Completa las placas indicadas arriba para definir {section.key === 'lubrication' ? 'lubricante, cantidad e intervalos' : section.key === 'parts' ? 'el plano y los repuestos correctos' : section.key === 'faults' ? 'el diagnóstico del fabricante' : 'el plan de mantenimiento'}. Puedes usar la secuencia de inspección para documentar el estado observado.</p>
            )}
            {section.key === 'parts' && guide.parts?.length ? (
              <details className="manual-guide__details" open>
                <summary>Lista de partes registrada en la ficha</summary>
                <ul>{guide.parts.map((part, index) => <li key={index}>{[part.position, part.description, part.reference].filter(Boolean).join(' · ')}{part.quantity != null ? ` · Cantidad: ${part.quantity}` : ''}</li>)}</ul>
              </details>
            ) : section.key === 'parts' ? <p className="muted">Esta ficha aún no tiene una lista de partes individual registrada. Los dibujos de familia deben validarse con el fabricante por serie.</p> : null}
          </Card>
        </div>
      ))}
    </div>
  )
}
